import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Modal, Button } from 'react-bootstrap';
import _ from 'lodash';

import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import * as Actions from '../../../redux/actions/index';
import { Icon, ICONS } from '../../Icons';

import './DuplicateCourse.scss';
import CourseTopicReadingList from './CourseTopicReadingList';
import { toast } from 'components/elements/molecules/Toast/Toast';

function DuplicateCourse({ courses, onClose, actions, sourceCourseId }) {
  const [sourceSelection, setSourceSelection] = useState(
    sourceCourseId ? [{ type: 'course', courseId: sourceCourseId }] : [],
  );
  const [destinationSelection, setDestinationSelection] = useState([]);
  const [newCourseName, setNewCourseName] = useState('');
  const [newTopicName, setNewTopicName] = useState('');
  const [createNewCourse, setCreateNewCourse] = useState(false);
  const [createNewTopic, setCreateNewTopic] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (sourceCourseId) {
      setTimeout(() => {
        document
          .querySelector(`div[data-course-id='source-${sourceCourseId}']`)
          .scrollIntoView({ behavior: 'smooth' });
      }, 0);
    }
  }, []);

  const onSubmit = async () => {
    let payload, error;

    if (!sourceSelection.length) {
      error = 'Please select source course or topics';
    }

    if (
      !error &&
      !destinationSelection.length &&
      !newCourseName &&
      !newTopicName
    ) {
      error = 'Please select destination course or topics';
    }

    if (!error && createNewCourse && !newCourseName.trim().length) {
      error = 'Please enter new course name';
    }

    if (!error && createNewTopic && !newTopicName.trim().length) {
      error = 'Please enter new topic name';
    }

    const sourceCourses = sourceSelection.filter((i) => i.type === 'course');
    const sourceTopics = sourceSelection.filter((i) => i.type === 'topic');

    const destinationCourses = destinationSelection.filter(
      (i) => i.type === 'course',
    );
    const destinationTopics = destinationSelection.filter(
      (i) => i.type === 'topic',
    );

    if (!error) {
      if (sourceCourses.length && sourceTopics.length) {
        error = 'Please select either course or topics';
      } else {
        if (sourceCourses.length) {
          if (createNewTopic) {
            error = 'Course can not be copied to new topic';
          } else if (newCourseName) {
            if (sourceCourses.length === 1) {
              payload = {
                actions: [
                  {
                    courseName: newCourseName,
                    newCourse: true,
                    sourceCourseId: sourceCourses[0].courseId,
                  },
                ],
              };
            } else {
              error = 'Please select only 1 source course if new course';
            }
          } else {
            if (destinationTopics.length) {
              error = 'Course can not be copied to a topic';
            } else {
              if (
                _.intersection(
                  sourceCourses.map((i) => i.courseId),
                  destinationCourses.map((i) => i.courseId),
                ).length
              ) {
                error = 'Course can not be copied to same course.';
              } else {
                const actions = [];
                sourceCourses.forEach((source) => {
                  destinationCourses.forEach((destination) => {
                    actions.push({
                      courseId: destination.courseId,
                      sourceCourseId: source.courseId,
                    });
                  });
                });
                payload = {
                  actions,
                };
              }
            }
          }
        } else {
          if (newCourseName) {
            error = 'Topic can not be copied to new course';
          } else if (createNewTopic) {
            if (sourceTopics.length === 1) {
              payload = {
                courseId: destinationSelection[0].courseId,
                actions: [
                  {
                    topicName: newTopicName,
                    newTopic: true,
                    sourceTopicId: sourceTopics[0].courseId,
                  },
                ],
              };
            } else {
              error = 'Please select only 1 source topic if new topic';
            }
          } else {
            if (destinationCourses.length) {
              const actions = [];
              sourceTopics.forEach((source) => {
                const sourceCourse = courses.find(
                  (i) => i.courseId === source.courseId,
                );
                const sourceTopic = sourceCourse.topics.find(
                  (i) => i._id === source.topicId,
                );
                destinationCourses.forEach((destination) => {
                  actions.push({
                    courseId: destination.courseId,
                    topics: [
                      {
                        topicName: `${sourceTopic.name} [Duplicated]`,
                        newTopic: true,
                        sourceTopicId: source.topicId,
                      },
                    ],
                  });
                });
              });
              payload = {
                actions,
              };
            } else {
              const actions = [];
              sourceTopics.forEach((source) => {
                destinationTopics.forEach((destination) => {
                  actions.push({
                    courseId: destination.courseId,
                    topics: [
                      {
                        topicId: destination.topicId,
                        sourceTopicId: source.topicId,
                      },
                    ],
                  });
                });
              });
              payload = {
                actions,
              };
            }
          }
        }
      }
    }

    if (error) {
      toast(error, {
        type: 'error',
      });
      return;
    }

    console.log('Copy Payload', payload);

    setSyncing(true);
    try {
      await actions.syncCourses(payload);
      await actions.getUserCourses();
      onClose();
      toast('Copy successful', {
        type: 'success',
      });
    } finally {
      setSyncing(false);
    }
  };

  const renderSource = () => {
    return (
      <div className="source-container d-flex flex-column">
        <h3 className="mt-0 av-blue pt-3 title">Source</h3>
        <div className="flex-fill list">
          <CourseTopicReadingList
            courses={courses}
            selection={sourceSelection}
            onSelection={setSourceSelection}
            actions={actions}
            type="source"
          />
        </div>
        {renderSourceSelectionDescription()}
      </div>
    );
  };

  const renderSourceSelectionDescription = () => {
    const elements = [];
    const selectedCourses = sourceSelection
      .filter((i) => i.type === 'course')
      .map((i) => courses.find((j) => j.courseId === i.courseId));

    if (selectedCourses.length) {
      elements.push(
        <p className="mb-0">
          You are copying{' '}
          <strong>{selectedCourses.map((i) => i.name).join(', ')}</strong>.
        </p>,
      );
    }

    const selectedTopics = sourceSelection
      .filter((i) => i.type === 'topic')
      .map((i) =>
        courses
          .find((j) => j.courseId === i.courseId)
          .topics.find((j) => j._id === i.topicId),
      );

    if (selectedTopics.length) {
      elements.push(
        <p className="mb-0">
          You are copying{' '}
          <strong>{selectedTopics.map((i) => i.name).join(', ')}</strong>.
        </p>,
      );
    }

    if (elements.length) {
      return (
        <div className="selection-description text-primary">{elements}</div>
      );
    }

    return null;
  };

  const renderDestination = () => {
    return (
      <div
        className="destination-container d-flex flex-column"
        style={{ borderLeft: '1px solid #e5e5e5' }}
      >
        <h3 className="mt-0 av-blue pt-3 title">Destination</h3>
        <div className="flex-fill list">
          <CourseTopicReadingList
            courses={courses}
            selection={destinationSelection}
            onSelection={setDestinationSelection}
            courseName={newCourseName}
            setCourseName={setNewCourseName}
            topicName={newTopicName}
            setTopicName={setNewTopicName}
            actions={actions}
            createNewCourse={createNewCourse}
            setCreateNewCourse={setCreateNewCourse}
            createNewTopic={createNewTopic}
            setCreateNewTopic={setCreateNewTopic}
            type="destination"
          />
        </div>
        {renderDestinationSelectionDescription()}
      </div>
    );
  };

  const renderDestinationSelectionDescription = () => {
    if (newCourseName) {
      return (
        <div className="selection-description text-primary">
          Your copy will be a new course, <strong>{newCourseName}</strong>.
        </div>
      );
    }

    if (createNewTopic) {
      return (
        <div className="selection-description text-primary">
          You copy will be a new topic, <strong>{newTopicName}</strong>.
        </div>
      );
    }

    const elements = [];
    const selectedCourses = destinationSelection
      .filter((i) => i.type === 'course')
      .map((i) => courses.find((j) => j.courseId === i.courseId));

    if (selectedCourses.length) {
      elements.push(
        <p className="mb-0">
          You are copying into{' '}
          <strong>{selectedCourses.map((i) => i.name).join(', ')}</strong>.
        </p>,
      );
    }

    const selectedTopics = destinationSelection
      .filter((i) => i.type === 'topic')
      .map((i) =>
        courses
          .find((j) => j.courseId === i.courseId)
          .topics.find((j) => j._id === i.topicId),
      );

    if (selectedTopics.length) {
      elements.push(
        <p className="mb-0">
          You are copying into{' '}
          <strong>{selectedTopics.map((i) => i.name).join(', ')}</strong>.
        </p>,
      );
    }

    if (elements.length) {
      return (
        <div className="selection-description text-primary">{elements}</div>
      );
    }

    return null;
  };

  const renderTitle = () => {
    return 'Duplicate a course/topic';
  };

  const renderSubmitButton = () => {
    let label = 'Copy';

    // if (createNewCourse) {
    //   label = 'Create new course';
    // } else
    if (createNewTopic) {
      label = 'Create new topic';
    }

    return (
      <RectangularButton
        className="next-button"
        onClick={() => onSubmit()}
        disabled={syncing || createNewCourse}
      >
        {syncing ? <span className="fa fa-spin fa-spinner"></span> : label}
      </RectangularButton>
    );
  };

  const renderStateText = () => {
    let text = '';

    if (sourceSelection.length) {
      const sourceCourses = sourceSelection.filter((i) => i.type === 'course');
      const sourceTopics = sourceSelection.filter((i) => i.type === 'topic');

      const sourceNames = [];

      if (sourceCourses.length) {
        sourceNames.push(
          ...sourceCourses.map(
            (i) => courses.find((j) => j.courseId === i.courseId).name,
          ),
        );
      }

      if (sourceTopics.length) {
        sourceNames.push(
          ...sourceTopics.map(
            (i) =>
              courses
                .find((j) => j.courseId === i.courseId)
                .topics.find((j) => j._id === i.topicId).name,
          ),
        );
      }

      if (newCourseName || newTopicName) {
        text = (
          <>
            You are creating a copy of <strong>{sourceNames.join(', ')}</strong>
            . Your copy is called{' '}
            <strong>{newCourseName || newTopicName}</strong>.
          </>
        );
      } else if (destinationSelection.length) {
        const destinationCourses = destinationSelection.filter(
          (i) => i.type === 'course',
        );
        const destinationTopics = destinationSelection.filter(
          (i) => i.type === 'topic',
        );

        const destinationNames = [];

        if (destinationCourses.length) {
          destinationNames.push(
            ...destinationCourses.map(
              (i) => courses.find((j) => j.courseId === i.courseId).name,
            ),
          );
        }

        if (destinationTopics.length) {
          destinationNames.push(
            ...destinationTopics.map(
              (i) =>
                courses
                  .find((j) => j.courseId === i.courseId)
                  .topics.find((j) => j._id === i.topicId).name,
            ),
          );
        }

        text = (
          <>
            You are creating a copy of <strong>{sourceNames.join(', ')}</strong>{' '}
            into <strong>{destinationNames.join(', ')}</strong>.
          </>
        );
      } else {
        text = (
          <>
            You are creating a copy of <strong>{sourceNames.join(', ')}</strong>
            .
          </>
        );
      }
    }

    if (!text) {
      text = <>Select a course or topic to copy.</>;
    }

    return <p className="p-3 m-0 state-text">{text}</p>;
  };

  return (
    <Modal show={true} className="duplicate-course-modal" bsSize="lg">
      <Modal.Header>
        <h3 className="m-0 av-blue">{renderTitle()}</h3>
        <Button bsStyle="link" className="btn-close" onClick={onClose}>
          <Icon icon={ICONS.CLOSE} />
        </Button>
      </Modal.Header>
      <Modal.Body className="p-0 flex-fill">
        <div className="source-destination-container d-flex flex-column">
          {renderStateText()}
          <div className="flex-fill d-flex" style={{ minHeight: '1px' }}>
            {renderSource()}
            {renderDestination()}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-end">
        {renderSubmitButton()}
      </Modal.Footer>
    </Modal>
  );
}

function mapStateToProps(state) {
  return {
    courses: state.classRoom.courses || [],
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DuplicateCourse);
